import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

interface HistoricalExchangeRateResponse {
  base: string;
  rates: Record<string, number>;
  time_last_update_unix: number;
  historical?: boolean;
}

const HISTORICAL_EXCHANGE_RATE_API_URL = 'https://open.exchangerate-api.com/v6/latest';
const SESSION_STORAGE_KEY = 'historical_exchange_rates_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours for historical rates

// Batch request tracking to minimize API calls
const pendingRequests = new Map<string, Promise<CachedHistoricalRate>>();

interface CachedHistoricalRate {
  rates: Record<string, number>;
  baseCurrency: string;
  lastUpdated: string;
  isHistorical: boolean;
  fetchedAt: number;
}

// Load cached historical rates from session storage
const loadCachedHistoricalRates = (
  date: string,
  baseCurrency: string
): CachedHistoricalRate | null => {
  try {
    const cacheKey = `${SESSION_STORAGE_KEY}_${date}_${baseCurrency}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed: CachedHistoricalRate = JSON.parse(cached);
    const now = Date.now();
    const cacheAge = now - parsed.fetchedAt;

    // Return cached data if less than 24 hours old
    if (cacheAge < CACHE_TTL) {
      return parsed;
    }

    // Clear expired cache
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error('Error loading cached historical exchange rates:', error);
    return null;
  }
};

// Save historical rates to session storage
const saveCachedHistoricalRates = (
  date: string,
  baseCurrency: string,
  data: CachedHistoricalRate
): void => {
  try {
    const cacheKey = `${SESSION_STORAGE_KEY}_${date}_${baseCurrency}`;
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving historical exchange rates to cache:', error);
  }
};

// Fetch historical exchange rates from API
const fetchHistoricalExchangeRates = async (
  date: string,
  baseCurrency: string
): Promise<CachedHistoricalRate> => {
  // Check cache first
  const cached = loadCachedHistoricalRates(date, baseCurrency);
  if (cached) {
    return cached;
  }

  // Check if there's already a pending request for this date/currency
  const requestKey = `${date}_${baseCurrency}`;
  const pendingRequest = pendingRequests.get(requestKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  // Create new request and track it
  const request = (async () => {
    try {
      // Note: The free API doesn't support historical dates, so we'll fetch latest
      // and mark it as non-historical with a warning flag
      const response = await fetch(`${HISTORICAL_EXCHANGE_RATE_API_URL}/${baseCurrency}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch exchange rates: ${response.statusText}`);
      }

      const data: HistoricalExchangeRateResponse = await response.json();

      // Check if the requested date is today
      const requestedDate = new Date(date);
      const today = new Date();
      const isToday =
        requestedDate.getFullYear() === today.getFullYear() &&
        requestedDate.getMonth() === today.getMonth() &&
        requestedDate.getDate() === today.getDate();

      const historicalRates: CachedHistoricalRate = {
        rates: data.rates,
        baseCurrency: data.base,
        lastUpdated: new Date(data.time_last_update_unix * 1000).toISOString(),
        isHistorical: isToday, // Only mark as historical if it's today's rate
        fetchedAt: Date.now(),
      };

      // Save to cache
      saveCachedHistoricalRates(date, baseCurrency, historicalRates);

      return historicalRates;
    } finally {
      // Remove from pending requests once complete
      pendingRequests.delete(requestKey);
    }
  })();

  // Track the pending request
  pendingRequests.set(requestKey, request);

  return request;
};

export interface UseHistoricalExchangeRateResult {
  rates: ExchangeRates | undefined;
  isLoading: boolean;
  error: Error | null;
  isHistorical: boolean;
  convert: (amount: number, fromCurrency: string, toCurrency?: string) => number;
  getRate: (fromCurrency: string, toCurrency?: string) => number | null;
}

/**
 * Hook to fetch exchange rates for a specific transaction date
 * Falls back to latest rates if historical rates are unavailable
 * Caches rates by date and base currency in sessionStorage
 */
export function useHistoricalExchangeRate(
  date: string,
  baseCurrency: string = 'USD'
): UseHistoricalExchangeRateResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ['historicalExchangeRates', date, baseCurrency],
    queryFn: () => fetchHistoricalExchangeRates(date, baseCurrency),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 48 * 60 * 60 * 1000, // 48 hours
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Convert amount from one currency to another
  const convert = useCallback(
    (amount: number, fromCurrency: string, toCurrency?: string): number => {
      const targetCurrency = toCurrency || baseCurrency;

      // No conversion needed if currencies match
      if (fromCurrency === targetCurrency) {
        return amount;
      }

      // Check if rates are available
      if (!data?.rates) {
        throw new Error('Exchange rates not available');
      }

      // Check if source currency rate exists
      if (!data.rates[fromCurrency]) {
        throw new Error(`Exchange rate not available for ${fromCurrency}`);
      }

      // Check if target currency rate exists
      if (!data.rates[targetCurrency]) {
        throw new Error(`Exchange rate not available for ${targetCurrency}`);
      }

      // Convert: amount in fromCurrency -> base currency -> toCurrency
      const amountInBase = amount / data.rates[fromCurrency];
      const convertedAmount = amountInBase * data.rates[targetCurrency];

      return convertedAmount;
    },
    [data, baseCurrency]
  );

  // Get exchange rate between two currencies
  const getRate = useCallback(
    (fromCurrency: string, toCurrency?: string): number | null => {
      const targetCurrency = toCurrency || baseCurrency;

      if (fromCurrency === targetCurrency) {
        return 1;
      }

      if (!data?.rates) {
        return null;
      }

      if (!data.rates[fromCurrency] || !data.rates[targetCurrency]) {
        return null;
      }

      return data.rates[targetCurrency] / data.rates[fromCurrency];
    },
    [data, baseCurrency]
  );

  return {
    rates: data
      ? {
          rates: data.rates,
          baseCurrency: data.baseCurrency,
          lastUpdated: data.lastUpdated,
        }
      : undefined,
    isLoading,
    error: error as Error | null,
    isHistorical: data?.isHistorical ?? false,
    convert,
    getRate,
  };
}
